import { SyntheticEvent, useEffect, useState } from 'react';
import { TableColumn } from '../../types/commonTypes';
import { Popper } from '../Popper';
import React from 'react';
import { Checkbox } from '../Checkbox';

interface Props {
  anchorElement?: Element | null;
  data: any[];
  filteredColumns: TableColumn[];
  loading: boolean;
  onClose(): void;
  open: boolean;
  setFilteredData(data: any[]): void;
}

interface FilterValue {
  dataKey: string;
  filter: string | number;
}

export const FilterDataMenu = (props: Props) => {
  const {
    anchorElement = null,
    data,
    filteredColumns,
    loading,
    onClose,
    open,
    setFilteredData,
  } = props;
  const [filters, setFilters] = useState<FilterValue[]>([]);
  const [filterAnchor, setFilterAnchor] = useState<Element | null>(null);
  const [filterKey, setFilterKey] = useState('');

  useEffect(() => {
    if (!open) resetFilterState();
  }, [open]);

  const handleFilter = (event: SyntheticEvent, dataKey: string) => {
    event.stopPropagation();

    const filteredData = data.slice();
    const target = event.target as HTMLInputElement;
    const filter = { dataKey: dataKey, filter: target.name };
    let allFilters: FilterValue[];

    if (target.checked) {
      allFilters = [filter, ...filters];
    } else {
      allFilters = filters.filter(
        (f) => !(f.dataKey === dataKey && f.filter === filter.filter)
      );
    }

    const columns = allFilters.reduce((newArr: string[], fi) => {
      if (!newArr.includes(fi.dataKey)) newArr.push(fi.dataKey);
      return newArr;
    }, []);

    setFilteredData(
      filteredData.reduce((arr, d) => {
        let matchingFilters: string[] = [];

        allFilters.forEach((f) => {
          if (!matchingFilters.includes(f.dataKey) && d[f.dataKey] === f.filter)
            matchingFilters.push(f.dataKey);
        });

        if (matchingFilters.length === columns.length) arr.push(d);

        return arr;
      }, [])
    );

    setFilters(allFilters);
  };

  const handleClose = () => {
    onClose();
    resetFilterState();
  };

  const setFilterState = (event: SyntheticEvent, dataKey: string) => {
    setFilterAnchor(event.currentTarget);
    setFilterKey(dataKey);
  };

  const resetFilterState = () => {
    setFilterAnchor(null);
    setFilterKey('');
  };

  return (
    <Popper
      id="filter-data-menu"
      anchorEl={anchorElement}
      clickAway
      onClose={handleClose}
      isVisible={open}
      color="card"
      placement="bottom"
      showArrow
    >
      <div className="grid grid-cols-2 gap-2 max-w-xs p-4">
        {filteredColumns
          .filter((c) => c.canFilter)
          .map((column) => (
            <React.Fragment key={column.name}>
              <button
                className="tg-border-button z-10 w-full"
                disabled={loading}
                onClick={(event) => {
                  if (event.currentTarget !== filterAnchor)
                    setFilterState(event, column.dataKey);
                  else resetFilterState();
                }}
              >
                {column.name}
              </button>
              <Popper
                anchorEl={filterAnchor}
                clickAway
                onClose={resetFilterState}
                isVisible={filterKey === column.dataKey && open}
                placement="bottom"
                parentPopperElementId="filter-data-menu"
                color="card"
              >
                <ul>
                  {Array.from(
                    new Set([...data.map((d) => d[column.dataKey])])
                  ).map((d, index) => {
                    return (
                      <li key={d + index} className="px-6 py-2">
                        <Checkbox
                          checked={Boolean(
                            filters.find(
                              (f) =>
                                f.dataKey === column.dataKey && f.filter === d
                            )
                          )}
                          color="primary"
                          label={d}
                          name={d}
                          onChange={(event) =>
                            handleFilter(event, column.dataKey)
                          }
                        />
                      </li>
                    );
                  })}
                </ul>
              </Popper>
            </React.Fragment>
          ))}
      </div>
    </Popper>
  );
};
